import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { isAdmin, isOwner } from '../utils/roleUtils';
import UserDashboardSettingsService from '../services/UserDashboardSettingsService';
import MyDashboard from './MyDashboard';
import AdminDashboard from './AdminDashboard';

export default function DashboardRouter(){
  const { user, loading } = useUser();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const viewParam = searchParams.get('view'); // ?view=my forces MyDashboard
  // /my-dashboard and /admin-dashboard are mounted as real routes and are linked
  // from in-app buttons, so the pathname is an explicit request for that view and
  // must outrank the saved preference. Without this both URLs rendered whatever
  // the preference said, i.e. /my-dashboard could show the Executive dashboard.
  const pathView = pathname === '/my-dashboard' ? 'my'
                 : pathname === '/admin-dashboard' ? 'admin'
                 : null;
  const [pref, setPref] = useState(null); // 'my' | 'admin' | null
  const [settingsRow, setSettingsRow] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!user?.company_id || !user?.id) { if (isMounted) setFetching(false); return; }
      try {
        const row = await UserDashboardSettingsService.get(user.company_id, user.id);
        setSettingsRow(row);
        const pd = row?.notification_preferences?.preferred_dashboard || null;
        if (isMounted) setPref(pd);
      } finally {
        if (isMounted) setFetching(false);
      }
    })();
    return () => { isMounted = false; };
  }, [user?.company_id, user?.id]);

  const canAdmin = isAdmin(user) || isOwner(user);

  // #6144: the LAST CLICK wins, not the last save to finish. Each click takes a number; a save
  // that finishes after a newer click does not move the view.
  const clickSeqRef = useRef(0);

  const setPreferred = async (which) => {
    if (!canAdmin) return;
    const seq = ++clickSeqRef.current;
    try {
      setSaving(true);
      const nextPrefs = { ...(settingsRow?.notification_preferences || {}), preferred_dashboard: which };
      await UserDashboardSettingsService.upsert(user.company_id, user.id, { notification_preferences: nextPrefs });
      if (seq !== clickSeqRef.current) return;
      setPref(which);
      // The pathname now OUTRANKS the saved preference (that is the F2 fix), so
      // saving alone is not enough: clicking "My Dashboard" while sitting on
      // /admin-dashboard stored the preference and then lost to the URL, and the
      // view never changed. Move the URL to match the click. `replace` keeps the
      // toggle out of the back-button history.
      const path = which === 'my' ? '/my-dashboard' : '/admin-dashboard';
      if (pathname !== path) navigate(path, { replace: true });
      setSettingsRow((prev) => ({ ...(prev || {}), notification_preferences: nextPrefs }));
    } finally {
      if (seq === clickSeqRef.current) setSaving(false);
    }
  };

  if (loading || fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // #6143: the switcher highlights the view that is SHOWN. It read the saved preference, so
  // /admin-dashboard with a saved 'my' showed the Business Dashboard with My Dashboard lit.
  const shownView = (() => {
    if (!canAdmin) return 'my';
    const explicit = viewParam === 'my' ? 'my' : pathView;
    if (explicit === 'my' || explicit === 'admin') return explicit;
    return pref === 'my' ? 'my' : 'admin';
  })();
  const content = shownView === 'my' ? <MyDashboard /> : <AdminDashboard />;

  return (
    <div className="dashboard-route-shell">
      {canAdmin && (
        <div className="dashboard-router-bar">
          <span className="dashboard-router-bar__label">Dashboard view</span>
          <div className="dashboard-router-switch">
            <button
              onClick={() => setPreferred('my')}
              className={shownView === 'my' ? 'is-active' : ''}
            >My Dashboard</button>
            <button
              onClick={() => setPreferred('admin')}
              className={shownView === 'admin' ? 'is-active' : ''}
            >Business Dashboard</button>
          </div>
          {saving && <span className="dashboard-router-bar__saving">Saving…</span>}
        </div>
      )}
      {content}
    </div>
  );
}
